import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { ROLE } from '@app/constant/RoleConstant'
import { DELAY_BETWEEN_INPUT } from '@app/constant/TimeOutConstant'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import { Autocomplete, Button, Grid, TextField } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import axios from 'axios'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import UserCreateSchema from './UserCreateSchema'

const ButtonCustom = styled(Button)({
  marginTop: 20,
  marginRight: 20,
})

export default function UserCreate({ roles, partners, merchants }) {
  const { t } = useTranslation()
  const [role, setRole] = useState('')

  const [currentRole, setCurrentRole] = useState([
    { slug: ROLE.ADMIN },
    { slug: ROLE.PARTNER },
    { slug: ROLE.MERCHANT },
  ])

  const [disabled, setDisabled] = useState(false)

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    role: '',
    partner_id: '',
    merchant_id: '',
    partner: '',
    merchant: '',
  }

  Inertia.on('finish', () => setDisabled(false))

  const onSubmit = (data) => {
    setDisabled(true)
    Inertia.post(route('admin.users.store'), data, {})
  }

  const {
    register,
    trigger,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(UserCreateSchema),
  })

  const onChangeRef = useRef(null)
  const email = watch('email')

  useEffect(() => {
    onChangeRef.current && onChangeRef.current('')
    if (email) {
      checkAllRoleUser(email)
    }
  }, [email])

  const checkAllRoleUser = useCallback(
    debounce((value) => {
      value &&
        axios.post(route('admin.user.listRole'), { email: value }).then((body) => {
          if (body.data) {
            setCurrentRole(body.data)
          }
        })
    }, DELAY_BETWEEN_INPUT)
  )

  return (
    <Grid container component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12}>
        <Section title={t('USER.BASIC_INFORMATION')}>
          <Grid container>
            <Grid item lg={7} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required title={t('USER.NAME')}>
                    <TextField
                      fullWidth
                      name="name"
                      {...register('name')}
                      error={errors.name ? true : false}
                      helperText={errors.name?.message}
                      size="small"
                      type="text"
                      variant="outlined"
                      onFocus={() => trigger('name')}
                    />
                  </LabelField>
                </Grid>

                <Grid item xs={12}>
                  <LabelField required title={t('USER.EMAIL')}>
                    <TextField
                      fullWidth
                      name="email"
                      size="small"
                      type="text"
                      {...register('email')}
                      error={errors.email ? true : false}
                      helperText={errors.email ? errors.email?.message : ''}
                      variant="outlined"
                      onFocus={() => trigger('email')}
                    />
                  </LabelField>
                </Grid>
                <Grid item xs={12}>
                  <LabelField required title={t('USER.PHONE')}>
                    <TextField
                      fullWidth
                      {...register('phone')}
                      error={errors.phone ? true : false}
                      helperText={errors.phone?.message}
                      name="phone"
                      size="small"
                      variant="outlined"
                      onFocus={() => trigger('phone')}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>

      <Grid item xs={12}>
        <Section title={t('USER.ROLE')}>
          <Grid container>
            <Grid container item lg={7} spacing={2} xs={12}>
              <Grid item xs={12}>
                <LabelField required title={t('USER.ROLE')}>
                  <Controller
                    control={control}
                    name="role"
                    render={({ field: { onChange, value, ref } }) => {
                      onChangeRef.current = onChange
                      return (
                        <TextField
                          fullWidth
                          select
                          error={errors.role ? true : false}
                          helperText={errors.role?.message}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          name="role"
                          ref={ref}
                          size="small"
                          value={getValues('email').length ? value : ''}
                          variant="outlined"
                          onChange={(e) => {
                            onChange(e.target.value)
                            setRole(e.target.value)
                          }}
                          onFocus={() => trigger('role')}
                        >
                          {roles.map((option) => (
                            <MenuItem
                              disabled={
                                !errors.email
                                  ? currentRole && currentRole.some((role) => role.slug === option.value)
                                  : true
                              }
                              key={option.value}
                              value={option.value}
                            >
                              {t(option.name)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )
                    }}
                  />
                </LabelField>
              </Grid>

              <Grid
                item
                style={{
                  display:
                    'partner' === role && getValues('role').length && getValues('email').length ? 'block' : 'none',
                }}
                xs={12}
              >
                <LabelField required title={t('USER.PARTNER')}>
                  <Autocomplete
                    fullWidth
                    getOptionLabel={(option) => option.name}
                    options={partners}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        {...params}
                        error={!!errors.partner && !getValues('partner').length}
                        helperText={errors.partner && !getValues('partner').length ? errors.partner?.message : ''}
                        variant="outlined"
                        {...register('partner')}
                        onFocus={() => trigger('partner')}
                      />
                    )}
                    size="small"
                    onChange={(e, value) => {
                      setValue('partner_id', value?.id ?? '')
                      setValue('partner', value?.id ?? '')
                    }}
                  />
                </LabelField>
              </Grid>
              <Grid
                item
                style={{
                  display:
                    'merchant' === role && getValues('role').length && getValues('email').length ? 'block' : 'none',
                }}
                xs={12}
              >
                <LabelField required title={t('USER.MERCHANT')}>
                  <Autocomplete
                    fullWidth
                    getOptionLabel={(option) => option.name}
                    options={merchants}
                    renderInput={(params) => (
                      <TextField
                        fullWidth
                        {...params}
                        error={!!errors.merchant && !getValues('merchant').length}
                        helperText={errors.merchant && !getValues('merchant').length ? errors.merchant?.message : ''}
                        variant="outlined"
                        {...register('merchant')}
                        onFocus={() => trigger('merchant')}
                      />
                    )}
                    size="small"
                    onChange={(e, value) => {
                      setValue('merchant_id', value?.id ?? '')
                      setValue('merchant', value?.id ?? '')
                    }}
                  />
                </LabelField>
              </Grid>
            </Grid>
          </Grid>
        </Section>
      </Grid>
      <Grid item xs={12}>
        <ButtonCustom disabled={disabled} type="submit" variant="contained">
          {t('BUTTON.SAVE')}
        </ButtonCustom>
        <ButtonCustom color="inherit" component={InertiaLink} href={route('admin.users.index')} variant="contained">
          {t('BUTTON.CANCEL')}
        </ButtonCustom>
      </Grid>
    </Grid>
  )
}
